import { ShoppingCart, Eye, ImagePlus } from "lucide-react";
import { Product } from "../types/product";
import { useState, useEffect, memo } from "react";
import { useCartStore } from "../store/cartStore";
import { useNavigate } from "react-router-dom";
import { formatEuro } from "../lib/utils";
import { productImage } from "../lib/productImage";
import { fromPrice, hasDiscount, isSoldOut, onlyVariant, activeVariants, priceLabel } from "../lib/variants";

interface ProductCardProps {
	product: Product;
	/** Anteprima rapida: è anche dove si sceglie il formato se ce n'è più d'uno. */
	onQuickView?: (product: Product) => void;
	className?: string;
}

const ProductCard = memo(function ProductCard({
	product,
	onQuickView,
	className = "",
}: ProductCardProps) {
	const navigate = useNavigate();
	const [isHovered, setIsHovered] = useState(false);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [imageError, setImageError] = useState(false);

	const cartItems = useCartStore((state) => state.items);
	const addItem = useCartStore((state) => state.addItem);
	const [isInCart, setIsInCart] = useState(false);
	const [cartQuantity, setCartQuantity] = useState(0);

	// Con un solo formato si aggiunge direttamente, altrimenti si sceglie.
	const single = onlyVariant(product);
	const soldOut = isSoldOut(product);
	const price = fromPrice(product);
	const discounted = single && hasDiscount(single) ? single : null;
	const onOffer = activeVariants(product).some(hasDiscount);
	const singleStock = single?.stock ?? 0;

	useEffect(() => {
		// Vasetti di questo miele nel carrello, tutti i formati insieme
		const quantity = cartItems
			.filter((item) => item.productId === product.id)
			.reduce((sum, item) => sum + item.quantity, 0);
		setIsInCart(quantity > 0);
		setCartQuantity(quantity);
	}, [cartItems, product.id]);

	const handleAddToCart = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (single) {
			addItem(product, single);
		} else if (onQuickView) {
			onQuickView(product);
		} else {
			navigate(`/products/${product.id}`);
		}
	};

	const handleQuickView = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (onQuickView) {
			onQuickView(product);
		}
	};

	const handleImageLoad = () => {
		setImageLoaded(true);
		setImageError(false);
	};

	const handleImageError = () => {
		setImageError(true);
		setImageLoaded(false);
	};

	return (
		<div
			className={`bg-card rounded-xl shadow-sm overflow-hidden h-full flex flex-col cursor-pointer transition-all duration-300 ease-in-out hover:shadow-xl hover:-translate-y-1 ${className}`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={() => navigate(`/products/${product.id}`)}
		>
			<div className="relative group overflow-hidden">
				{/* Immagine del prodotto */}
				<div className="w-full h-64 bg-muted relative overflow-hidden">
					{!imageError ? (
						<img
							src={productImage(product.image_url)}
							alt={product.name}
							className={`w-full h-full object-cover transition-transform duration-500 ease-in-out ${
								isHovered ? "scale-110" : "scale-100"
							} ${imageLoaded ? "opacity-100" : "opacity-0"}`}
							loading="lazy"
							decoding="async"
							onLoad={handleImageLoad}
							onError={handleImageError}
						/>
					) : (
						<div className="w-full h-full flex items-center justify-center bg-secondary">
							<div className="text-muted-foreground">
								<ImagePlus size={24} className="mx-auto" />
								<p className="text-xs mt-1">Immagine non disponibile</p>
							</div>
						</div>
					)}

					{!imageLoaded && !imageError && (
						<div className="absolute inset-0 flex items-center justify-center bg-muted">
							<div className="animate-pulse bg-secondary w-full h-full"></div>
						</div>
					)}

					{/* Badge prezzo */}
					{price !== null && (
						<div className="absolute top-3 right-3">
							<div className="flex flex-col items-end space-y-1">
								{discounted ? (
									<span className="bg-red-500 text-white px-2 py-1 rounded-md text-sm font-bold">
										-{discounted.discount_percent}%
									</span>
								) : (
									onOffer && (
										<span className="bg-red-500 text-white px-2 py-1 rounded-md text-sm font-bold">
											In offerta
										</span>
									)
								)}
								<span className="bg-secondary text-foreground px-2 py-1 rounded-md text-sm font-medium">
									{priceLabel(product)}
								</span>
							</div>
						</div>
					)}

					{/* Wishlist button - temporaneamente nascosta */}
					{/*<button 
            onClick={handleWishlist}
            className="absolute top-3 left-3 p-2 bg-card rounded-full shadow-md hover:shadow-lg transition-all duration-300 ease-in-out transform hover:scale-110"
            aria-label={isWishlisted ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          >
            <Heart 
              className={`w-4 h-4 transition-colors duration-200 ${
                isWishlisted 
                  ? 'text-red-500 fill-current' 
                  : 'text-muted-foreground hover:text-red-400'
              }`} 
            />
          </button>*/}

					{/* Quick view button (solo su hover) */}
					{onQuickView && (
						<button
							type="button"
							onClick={handleQuickView}
							className={`absolute top-14 left-3 p-2 bg-card rounded-full shadow-md hover:shadow-lg transition-all duration-300 ease-in-out transform ${
								isHovered ? "scale-100 opacity-100" : "scale-0 opacity-0"
							}`}
							aria-label="Anteprima rapida"
						>
							<Eye className="w-4 h-4 text-muted-foreground" />
						</button>
					)}

					{/* Badge stock */}
					{single && singleStock <= 5 && singleStock > 0 && (
						<div className="absolute bottom-3 left-3 bg-peach text-peach-foreground px-2 py-1 rounded-md text-xs font-medium">
							Ultimi pezzi!
						</div>
					)}

					{soldOut && (
						<div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
							<span className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold">
								Esaurito
							</span>
						</div>
					)}

					{/* Prezzo originale barrato se in sconto */}
					{discounted && (
						<div className="absolute bottom-3 right-3">
							<span className="text-xs text-muted-foreground line-through">
								{formatEuro(discounted.original_price!)}
							</span>
						</div>
					)}
				</div>
			</div>

			{/* Contenuto della card */}
			<div className="p-4 flex flex-col flex-grow space-y-3">
				<h3 className="text-lg font-semibold text-foreground line-clamp-2 leading-tight">
					{product.name}
				</h3>

				{product.description && (
					<p className="text-muted-foreground text-sm flex-grow line-clamp-5 leading-relaxed">
						{product.description}
					</p>
				)}

				{product.category && (
					<div className="inline-flex">
						<span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full">
							{product.category.toUpperCase()}
						</span>
					</div>
				)}

				{/* Quantità nel carrello (se presente) */}
				{isInCart && (
					<div className="text-sm text-green-600 dark:text-green-400 font-medium">
						Nel carrello: {cartQuantity}{" "}
						{cartQuantity === 1 ? "pezzo" : "pezzi"}
					</div>
				)}

				{/* Bottone Aggiungi al carrello */}
				<div className="mt-auto pt-2">
					<button
						type="button"
						onClick={handleAddToCart}
						disabled={soldOut}
						className={`w-full flex items-center justify-center space-x-2 py-3 rounded-lg transition-all duration-300 ease-in-out ${
							!soldOut
								? isInCart
									? "bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg"
									: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg"
								: "bg-muted text-muted-foreground cursor-not-allowed"
						}`}
						aria-label={soldOut ? "Prodotto esaurito" : single ? "Aggiungi al carrello" : "Scegli il formato"}
					>
						<ShoppingCart className="w-4 h-4" />
						<span className="font-medium">
							{soldOut ? "Esaurito" : single ? "Aggiungi al carrello" : "Scegli il formato"}
						</span>
					</button>
				</div>
			</div>
		</div>
	);
});

// Aggiungiamo display name per debug
ProductCard.displayName = "ProductCard";

export default ProductCard;
